/** Dutch labels and small formatting helpers for the UI. */

import type { RoundDirection, ScoringPresetId, Suit, TrumpSuit } from '@shared/types/game';

export const SUIT_LABELS: Record<Suit, string> = {
  spades: 'schoppen',
  hearts: 'harten',
  diamonds: 'ruiten',
  clubs: 'klaveren',
};

export function trumpLabel(trump: TrumpSuit | undefined): string {
  if (trump === undefined) return 'niet vastgelegd';
  if (trump === null) return 'troefloos';
  return SUIT_LABELS[trump];
}

export const SCORING_PRESET_LABELS: Record<ScoringPresetId, string> = {
  'nl-standaard': 'Standaard',
  'dubbel-bod': 'Dubbel bod',
  kwadraat: 'Kwadraat',
  mild: 'Mild',
  eigen: 'Eigen regels',
};

export const SCORING_PRESET_SUMMARIES: Record<ScoringPresetId, string> = {
  'nl-standaard': '10 + je bod bij precies goed, −2 per slag verschil',
  'dubbel-bod': '10 + 2× je bod bij precies goed, −2 per slag verschil',
  kwadraat: '10 + je bod in het kwadraat: bod 4 gehaald is 26 punten, bod 6 is 46',
  mild: '10 + je bod bij precies goed, −1 per slag verschil',
  eigen: 'Zelf ingestelde punten',
};

export const DIRECTION_LABELS: Record<RoundDirection, string> = {
  'up-down': 'Op en neer (1 → max → 1)',
  up: 'Alleen oplopend (1 → max)',
  down: 'Alleen aflopend (max → 1)',
};

/** Always show the sign, so a score column reads as points gained or lost. */
export function signed(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}

export function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} uur` : `${hours} uur ${rest} min`;
}

export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Seat colours for up to seven players, used consistently by the scorecard, the
 * chart and the standings so a colour always means the same person.
 *
 * The order is the safety mechanism, not decoration: these seven steps were
 * validated as a set against the dark chart surface (#131c2e) for the lightness
 * band, chroma floor, colour-vision-deficiency separation of adjacent slots
 * (worst ΔE 8.4), the normal-vision floor (19.3) and 3:1 contrast. Reordering or
 * substituting a hue invalidates that — re-run the check before changing this.
 */
export const SEAT_COLORS = [
  '#3987e5', // blue
  '#d95926', // orange
  '#199e70', // aqua
  '#c98500', // yellow
  '#d55181', // magenta
  '#008300', // green
  '#9085e9', // violet
] as const;

export function seatColor(seat: number): string {
  return SEAT_COLORS[seat % SEAT_COLORS.length];
}

export const PLAYER_EMOJI = [
  '🦊',
  '🐻',
  '🐼',
  '🦁',
  '🐸',
  '🐧',
  '🦉',
  '🐙',
  '🦄',
  '🐢',
  '🐝',
  '🦖',
] as const;
